import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { GameDto } from '../../api/types';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectAllGames, selectFavoriteGameIds } from '../../store/selectors/gamesSelectors';
import { fetchGames } from '../../store/thunks/gamesThunks';

import './GameSelect.css';

type GameSelectProps = {
  selectedGameId: string | null;
  onGameChange: (gameId: string | null) => void;
};

export function GameSelect({ selectedGameId, onGameChange }: GameSelectProps) {
  const dispatch = useAppDispatch();
  const games = useAppSelector(selectAllGames);
  const favoriteIds = useAppSelector(selectFavoriteGameIds);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  useEffect(() => {
    if (games.length === 0) {
      dispatch(fetchGames());
    }
  }, [dispatch, games.length]);

  const selectedGame = useMemo(
    () => games.find((g) => g.id === selectedGameId) ?? null,
    [games, selectedGameId],
  );

  const favoriteSet = useMemo(() => new Set(favoriteIds), [favoriteIds]);

  const filtered = useMemo(() => {
    let result = games;

    if (favoritesOnly) {
      result = result.filter((g) => favoriteSet.has(g.id));
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((g) => g.name.toLowerCase().includes(q));
    }

    if (favoriteSet.size > 0) {
      const favs = result.filter((g) => favoriteSet.has(g.id));
      const rest = result.filter((g) => !favoriteSet.has(g.id));
      return [...favs, ...rest];
    }

    return result;
  }, [games, search, favoritesOnly, favoriteSet]);

  const handleSelect = useCallback((game: GameDto | null) => {
    onGameChange(game?.id ?? null);
    setIsOpen(false);
    setSearch('');
    setFavoritesOnly(false);
  }, [onGameChange]);

  return (
    <div className="game-select">
      <button
        type="button"
        className="game-select__trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedGame ? (
          <span className="game-select__value">🎲 {selectedGame.name}</span>
        ) : (
          <span className="game-select__placeholder">Выбрать игру (необязательно)</span>
        )}
        <span className="game-select__arrow">{isOpen ? '▲' : '▼'}</span>
      </button>

      {selectedGame && (
        <button
          type="button"
          className="game-select__clear"
          onClick={() => handleSelect(null)}
        >
          × Очистить
        </button>
      )}

      {isOpen && (
        <div className="game-select__dropdown">
          <div className="game-select__toolbar">
            <input
              className="game-select__search"
              type="text"
              placeholder="Поиск игры..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            {favoriteIds.length > 0 && (
              <button
                type="button"
                className={`game-select__fav-toggle${favoritesOnly ? ' game-select__fav-toggle--active' : ''}`}
                onClick={() => setFavoritesOnly((v) => !v)}
                title={favoritesOnly ? 'Показать все игры' : 'Только избранные'}
              >
                {favoritesOnly ? '❤️' : '🤍'}
              </button>
            )}
          </div>
          <div className="game-select__list">
            {filtered.length === 0 ? (
              <div className="game-select__empty">
                {favoritesOnly ? 'Нет избранных игр' : 'Игры не найдены'}
              </div>
            ) : (
              filtered.map((game) => {
                const isFav = favoriteSet.has(game.id);
                const isSelected = game.id === selectedGameId;
                const cls = [
                  'game-select__option',
                  isSelected && 'game-select__option--selected',
                  isFav && 'game-select__option--favorite',
                ].filter(Boolean).join(' ');

                return (
                  <button
                    key={game.id}
                    type="button"
                    className={cls}
                    onClick={() => handleSelect(game)}
                  >
                    <span className="game-select__option-name">
                      {isFav && <span className="game-select__option-fav-icon">❤️</span>}
                      {game.name}
                    </span>
                    <span className="game-select__option-players">
                      {game.minPlayers}–{game.maxPlayers} игр.
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
